import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { Buffer } from 'buffer';
import { PaginationProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params';
import { getCountriesOptions, StringArrayParam } from '@app/constants';
import { OrderDirectionParam, TrialOrderByParams, useDebounce } from '@app/utils';
import {
  useLeadSponsorsInfiniteList,
  useLocationsInfiniteList,
  useNestedCategories,
  usePermissions,
  useTrialsList,
} from '@data/hooks';
import {
  MatchingTrialStatus,
  OverallTrialStatus,
  TrialManagementApiSearchRequest,
  TrialOrderBy,
} from '@data/sdk/match';
import { useDynamicTrialsFiltersStore } from '@data/storage';
import { HTTPStatusCodes, OrderDirection } from '@data/utils/enums';
import { antdNotification } from '@ui/components';
import { Filter } from '../types';
import {
  getDynamicNestedOptions,
  findOptimalLimit,
  handleNestedOptionsSearch,
  getBottomLevelOptions,
  getStaticOptions,
} from '../utils';

const MIN_ICD_CODES_SEARCH_LENGTH = 2;
const MAX_LOCATIONS_LIMIT = 50;

export const useTrialsFilters = () => {
  const { t } = useTranslation();
  const { IS_REVIEWER } = usePermissions();
  const countriesOptions = getCountriesOptions();

  const [icdCodesSearch, setIcdCodesSearch] = useState('');
  const [countriesSearch, setCountriesSearch] = useState('');

  const { categories, setCategories, clearCategories, setCategoriesList, clearCategoriesList } =
    useDynamicTrialsFiltersStore();

  const { data: nestedIcdCodes, isLoading: isNestedIcdCodesLoading } = useNestedCategories({
    type: 'icdCode',
    ...(IS_REVIEWER ? { forceRecalculate: false } : {}),
  });

  // Filters
  const [query, setQuery] = useQueryParams({
    searchString: StringParam,
    limit: withDefault(NumberParam, 10),
    page: withDefault(NumberParam, 1),
    locationStrings: withDefault<string[], string[]>(StringArrayParam, []),
    icdCategoryIds: withDefault<string[], string[]>(StringArrayParam, []),
    // trialStatus: withDefault<string[], string[]>(StringArrayParam, []),
    overallStatuses: withDefault<string[], string[]>(StringArrayParam, []),
    matchingStatuses: withDefault<string[], string[]>(StringArrayParam, []),
    leadSponsorFilter: withDefault<string[], string[]>(StringArrayParam, []),
    countries: withDefault<string[], string[]>(StringArrayParam, []),
    orderDirection: withDefault(OrderDirectionParam, OrderDirection.DESC),
    orderBy: withDefault(TrialOrderByParams, TrialOrderBy.UpdatedAt),
  });

  const {
    page,
    limit,
    orderBy,
    // trialStatus,
    countries,
    searchString,
    locationStrings,
    icdCategoryIds,
    overallStatuses,
    matchingStatuses,
    orderDirection,
    leadSponsorFilter,
  } = query;

  const debouncedSearch = useDebounce(searchString);
  const debouncedIcdCodesSearch = useDebounce(icdCodesSearch);

  const icdCodes = useMemo(
    () => getDynamicNestedOptions(nestedIcdCodes?.topLevelCategories ?? []),
    [nestedIcdCodes?.topLevelCategories],
  );

  // Search icd codes only if search string is longer than 2 characters
  const shouldSearchIcdCodes = useMemo(
    () => icdCodesSearch?.length > MIN_ICD_CODES_SEARCH_LENGTH,
    [icdCodesSearch],
  );

  const icdCodesWithSearch = useMemo(
    () =>
      shouldSearchIcdCodes
        ? handleNestedOptionsSearch(icdCodes, debouncedIcdCodesSearch)
        : icdCodes,
    [icdCodes, debouncedIcdCodesSearch, shouldSearchIcdCodes],
  );

  const countriesOptionsWithSearch = useMemo(
    () =>
      countriesSearch
        ? countriesOptions.filter((country) =>
            country.label.toLowerCase().includes(countriesSearch.toLowerCase()),
          )
        : countriesOptions,
    [countriesSearch],
  );

  const params: TrialManagementApiSearchRequest['getTrialsBody'] = {
    limit,
    orderBy,
    offset: (page - 1) * limit,
    orderDirection,
    searchString: debouncedSearch || undefined,
    locationString: locationStrings.length ? locationStrings : undefined,
    icdCategoryIds: icdCategoryIds.length
      ? Buffer.from(JSON.stringify(getBottomLevelOptions(icdCategoryIds, icdCodes))).toString(
          'base64',
        )
      : undefined,
    leadSponsorFilter: leadSponsorFilter.length ? leadSponsorFilter : undefined,
    overallStatuses: overallStatuses.length ? (overallStatuses as OverallTrialStatus[]) : undefined,
    matchingStatuses: matchingStatuses.length
      ? (matchingStatuses as MatchingTrialStatus[])
      : undefined,
    countriesFilter: countries.length ? countries : undefined,
  };

  const { data: trials, isLoading: isTrialsLoading, error } = useTrialsList(params);
  const {
    leadSponsorsOptions,
    searchString: sponsorSearch,
    fetchNextPage: fetchNextLeadSponsorsPage,
    isFirstLoading: leadSponsorsAreLoading,
    handleSearchChange: handleSearchLeadSponsor,
  } = useLeadSponsorsInfiniteList();
  const {
    locationsOptions,
    count: locationsCount,
    searchString: locationsSearchString,
    setQuery: setLocationsQuery,
    fetchNextPage: fetchNextLocationsPage,
    isFetchingNextPage: newLocationsAreLoading,
    isFirstLoading: locationsAreLoading,
    handleSearchChange: handleSearchLocation,
  } = useLocationsInfiniteList();

  // icdCodes, starters and tags are the same entity - categories
  const getCategoryIdsChecked = (value: string) => icdCategoryIds.includes(value);
  // const getTrialStatusChecked = (value: string) => trialStatus.includes(value);
  const getLeadSponsorsChecked = (value: string) => leadSponsorFilter.includes(value);
  const getLocationsChecked = (value: string) => locationStrings.includes(value);
  const getOverallStatusChecked = (value: string) => overallStatuses.includes(value);
  const getCountriesChecked = (value: string) => countries.includes(value);

  const handleSearchChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setQuery({
      page: 1,
      searchString: value || undefined,
    });
  };

  const handleOrderByChange = (value: string) => {
    setQuery({
      page: 1,
      orderBy: value as TrialOrderBy,
    });
  };

  const handleOrderDirectionChange = (value: string) => {
    setQuery({
      page: 1,
      orderDirection: value as OrderDirection,
    });
  };

  const handleCategoryIdsChange = (value: string, label?: string) => {
    if (label) {
      setCategories(value, label, 'category');
    }

    setQuery({
      page: 1,
      icdCategoryIds: getCategoryIdsChecked(value)
        ? icdCategoryIds.filter((v) => v !== value)
        : [...icdCategoryIds, value],
    });
  };

  // const handleTrialStatusChange = (value: string) => {
  //   setQuery({
  //     page: 1,
  //     trialStatus: getTrialStatusChecked(value)
  //       ? trialStatus.filter((v) => v !== value)
  //       : [...trialStatus, value],
  //   });
  // };

  const handleOverallStatusChange = (value: string) => {
    setQuery({
      page: 1,
      overallStatuses: getOverallStatusChecked(value)
        ? overallStatuses.filter((v) => v !== value)
        : [...overallStatuses, value],
    });
  };

  const handleCountriesChange = (value: string) => {
    setQuery({
      page: 1,
      countries: getCountriesChecked(value)
        ? countries.filter((v) => v !== value)
        : [...countries, value],
    });
  };

  const handleLocationChange = (value: string, label?: string) => {
    if (label) {
      setCategories(value, label, 'location');
    }

    setQuery({
      page: 1,
      locationStrings: getLocationsChecked(value)
        ? locationStrings.filter((v) => v !== value)
        : [...locationStrings, value],
    });
  };

  const handleLeadSponsorsChange = (value: string, label?: string) => {
    if (label) {
      setCategories(value, label, 'sponsor');
    }

    setQuery({
      page: 1,
      leadSponsorFilter: getLeadSponsorsChecked(value)
        ? leadSponsorFilter.filter((v) => v !== value)
        : [...leadSponsorFilter, value],
    });
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setQuery({ page: newPage, limit: newLimit });
  };

  const handleStatusSwitchChange = (value: boolean) => {
    setQuery({
      page: 1,
      matchingStatuses: value ? [MatchingTrialStatus.Active] : [],
    });
  };

  const handleLeadSponsorsSelectScroll = (e: any) => {
    if (e.target.scrollTop + e.target.offsetHeight + 2 > e.target.scrollHeight) {
      fetchNextLeadSponsorsPage();
    }
  };

  const handleLocationsSelectScroll = (e: any) => {
    if (
      e.target.scrollTop + e.target.offsetHeight + 2 > e.target.scrollHeight &&
      !newLocationsAreLoading
    ) {
      fetchNextLocationsPage();
    }
  };

  const handleSelectAllLocations = () => {
    setCategoriesList(locationsOptions, 'location');

    setQuery({
      page: 1,
      locationStrings: [
        ...locationStrings,
        ...locationsOptions
          .filter((c) => !locationStrings.some((ex) => ex === c.value))
          .map((c) => c.value),
      ],
    });
  };

  const handleDeselectAllLocations = () => {
    clearCategoriesList('location');

    setQuery({
      page: 1,
      locationStrings: [],
    });
  };

  const clearAllFilters = () => {
    setQuery({
      page: 1,
      searchString: undefined,
      locationStrings: [],
      icdCategoryIds: [],
      leadSponsorFilter: [],
      overallStatuses: [],
      countries: [],
    });
  };

  const shouldFetchAllLocations = useMemo(
    () => locationsCount > 0 && locationsCount <= MAX_LOCATIONS_LIMIT,
    [locationsCount],
  );

  const canDeselectAllLocations = useMemo(
    () => locationStrings.length > 0,
    [locationStrings.length],
  );

  const filters: Filter[] = [
    {
      key: 'leadSponsors',
      title: 'trials_library.filters.sponsor',
      placeholder: 'trials_library.filters.search_sponsor',
      isDynamic: true,
      isInfinite: true,
      openByDefault: true,
      isLoading: leadSponsorsAreLoading,
      options: leadSponsorsOptions,
      searchValue: sponsorSearch || '',
      onChange: handleLeadSponsorsChange,
      getIsChecked: getLeadSponsorsChecked,
      onSearch: handleSearchLeadSponsor,
      onScroll: handleLeadSponsorsSelectScroll,
    },
    {
      key: 'investigationSite',
      title: 'trials_library.filters.investigation_site',
      placeholder: 'trials_library.filters.search_investigation_site',
      isDynamic: true,
      isInfinite: true,
      openByDefault: true,
      isLoading: locationsAreLoading,
      options: locationsOptions,
      searchValue: locationsSearchString || '',
      isFetchingNextPage: newLocationsAreLoading,
      canSelectAll: shouldFetchAllLocations,
      canDeselectAll: canDeselectAllLocations,
      onChange: handleLocationChange,
      getIsChecked: getLocationsChecked,
      onSearch: handleSearchLocation,
      onSelectAll: handleSelectAllLocations,
      onDeselectAll: handleDeselectAllLocations,
      onScroll: handleLocationsSelectScroll,
    },
    {
      key: 'countries',
      title: 'trials_library.filters.country',
      placeholder: 'trials_library.filters.search_country',
      options: countriesSearch ? countriesOptionsWithSearch : countriesOptions,
      onChange: handleCountriesChange,
      getIsChecked: getCountriesChecked,
      isDynamic: true,
      openByDefault: false,
      isInfinite: true,
      searchValue: countriesSearch,
      onSearch: setCountriesSearch,
    },
    {
      key: 'overallStatus',
      title: 'trials_library.filters.overall_status',
      options: getStaticOptions(OverallTrialStatus, 'overall_status'),
      onChange: handleOverallStatusChange,
      getIsChecked: getOverallStatusChecked,
      isDynamic: false,
      openByDefault: false,
    },
    {
      key: 'icdCodes',
      title: 'trials_library.filters.condition_disease',
      placeholder: 'trials_library.filters.search_icd_codes',
      options: shouldSearchIcdCodes ? icdCodesWithSearch : icdCodes,
      onChange: handleCategoryIdsChange,
      getIsChecked: getCategoryIdsChecked,
      isDynamic: true,
      isLoading: isNestedIcdCodesLoading,
      openByDefault: false,
      searchValue: icdCodesSearch,
      onSearch: setIcdCodesSearch,
      debouncedSearchValue: debouncedIcdCodesSearch,
      shouldSearch: shouldSearchIcdCodes,
    },
    // {
    //   key: 'recruitmentStatus',
    //   title: 'trials_library.filters.recruitment_status',
    //   options: getStaticOptions(TrialStatus, 'recruitment_status'),
    //   onChange: handleTrialStatusChange,
    //   getIsChecked: getTrialStatusChecked,
    //   isDynamic: false,
    //   openByDefault: false,
    // },
  ];

  const paginationParams: PaginationProps = {
    pageSize: query.limit,
    current: query.page,
    onChange: handlePaginationChange,
    total: trials?.count,
  };

  const isActiveStatus = useMemo(
    () => matchingStatuses.length === 1 && matchingStatuses.includes(MatchingTrialStatus.Active),
    [matchingStatuses],
  );

  useEffect(() => {
    if (
      icdCategoryIds.length + leadSponsorFilter.length + locationStrings.length !==
      categories.length
    ) {
      clearCategories();
    }
  }, [icdCategoryIds.length, leadSponsorFilter.length, locationStrings.length, categories.length]);

  // try to fit all trials in one page if possible
  useEffect(() => {
    const trialsCount = trials?.count;

    if (typeof trialsCount === 'number') {
      const optimalLimit = findOptimalLimit(trialsCount);

      if (optimalLimit) {
        setQuery({ limit: optimalLimit });
      }
    }
  }, [trials?.count]);

  // try to fetch all locations at once if possible
  useEffect(() => {
    if (shouldFetchAllLocations) {
      setLocationsQuery((prev) => ({
        ...prev,
        limit: MAX_LOCATIONS_LIMIT,
      }));
    }
  }, [shouldFetchAllLocations]);

  useEffect(() => {
    if (error?.response?.status === HTTPStatusCodes.REQUEST_TOO_LARGE) {
      antdNotification.error({ title: t('beErrors.request_too_large') });
    }
  }, [error?.response?.status]);

  useEffect(() => {
    // if no matching statuses are selected, set the default ones
    if (!matchingStatuses.length) {
      setQuery({ matchingStatuses: [MatchingTrialStatus.Active] });
    }
  }, []);

  return {
    params,
    filters,
    orderBy,
    countries,
    isActiveStatus,
    orderDirection,
    overallStatuses,
    isTrialsLoading,
    paginationParams,
    count: trials?.count,
    trials: trials?.items || [],
    searchString: searchString || '',
    clearAllFilters,
    handleSearchChange,
    handleOrderByChange,
    handleLocationChange,
    handleCountriesChange,
    handleCategoryIdsChange,
    handleLeadSponsorsChange,
    handleStatusSwitchChange,
    handleOverallStatusChange,
    handleOrderDirectionChange,
  };
};
